import Page from 'components/common/page';
import titleTemplate from 'lib/title-template';
import { sendResetPasswordEmail } from 'models/auth-model';
import { NextPage } from 'next';
import Head from 'next/head';
import React, { useState, MouseEvent } from 'react';
import { LabelMedium, LabelSmall } from 'components/common/typography';
import Button from 'components/common/button';
import axios from 'axios';
import css from './user-auth-pages.module.css';
import cx from 'classnames';

type RequestState = 'idle' | 'in progress' | 'failed' | 'success';

const ForgotPassword: NextPage = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<RequestState>('idle');

  const handleSubmit = async (event: MouseEvent): Promise<void> => {
    event.preventDefault();
    if (!email) {
      return;
    }
    setMessage(null);
    setState('in progress');
    try {
      await sendResetPasswordEmail(email);
      setState('success');
      setMessage(
        `If an account exists, you will get an email with instructions on resetting your password. If it doesn't arrive, be sure to check your spam folder.`,
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setMessage(error.response?.data?.message || error.message);
      }
      // Should set state as failed in any error case?
      setState('failed');
    }
  };

  return (
    <Page pageName="Forgot Password">
      <Head>
        <title>{titleTemplate('Forgot password')}</title>
      </Head>
      <meta
        name="description"
        content="Forgot your password? We’re here to help. Just type your email address and we’ll reset your password so you can continue using Bibcitation."
      />
      <div className={css.mainWrapper}>
        <div className={css.formContainer}>
          {message && (
            <div
              className={cx(css.message, {
                [css.messageSuccess]: state === 'success',
                [css.messageFailed]: state === 'failed',
              })}
            >
              {message}
            </div>
          )}
          {state !== 'success' && (
            <>
              <h1 className={css.heading}>
                <LabelMedium>Forgot password?</LabelMedium>
              </h1>
              <form>
                <label htmlFor="email">
                  <LabelSmall>Email</LabelSmall>
                </label>
                <input
                  type="email"
                  id="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
                <div className={css.formContainerButtons}>
                  <Button fullWidth onClick={handleSubmit}>
                    Reset password
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </Page>
  );
};

export default ForgotPassword;
